import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { RatingStars, EmptyState } from "@/components/common";
import { DetailsFields, LocationFields, ImagesFields, pickBusiness, validateBusiness, validateLocation } from "./BusinessFields";

export function ProviderReviews() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/provider/reviews").then(({ data }) => setItems(data)); }, []);
  if (!items) return null;
  if (!items.length) return <EmptyState icon={Star} title="No reviews yet" text="Reviews appear after customers complete appointments." testId="reviews-empty" />;
  return (
    <div className="grid gap-3 md:grid-cols-2" data-testid="provider-reviews">
      {items.map((r) => (
        <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`pr-review-${r.id}`}>
          <div className="flex items-center justify-between"><p className="font-semibold">{r.customer_name}</p><RatingStars value={r.rating} /></div>
          <p className="mt-1 text-xs text-slate-400">{r.service_name} · {formatDate(r.created_at.slice(0, 10))}</p>
          <p className="mt-3 text-sm text-slate-700">{r.comment}</p>
        </div>
      ))}
    </div>
  );
}

export function ProviderProfileForm({ provider, onSaved }) {
  const [value, setValue] = useState(pickBusiness(provider));
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)); }, []);
  const save = async () => {
    const err = validateBusiness(value) || validateLocation(value);
    if (err) return toast.error(err);
    setBusy(true);
    try { await api.put("/provider/profile", { ...value, years_in_business: Number(value.years_in_business) }); toast.success("Business profile saved"); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const card = "rounded-2xl border border-slate-200 bg-white p-5 sm:p-6";
  return (
    <div className="space-y-6" data-testid="provider-profile-form">
      <div className={card}><h3 className="mb-4 font-heading text-lg font-semibold">Business details</h3><DetailsFields value={value} onChange={setValue} categories={categories} /></div>
      <div className={card}><h3 className="mb-4 font-heading text-lg font-semibold">Location</h3><LocationFields value={value} onChange={setValue} /></div>
      <div className={card}><h3 className="mb-4 font-heading text-lg font-semibold">Images</h3><ImagesFields value={value} onChange={setValue} /></div>
      <button data-testid="save-profile" disabled={busy} onClick={save} className="h-12 rounded-xl bg-sky-600 px-6 font-semibold text-white hover:bg-sky-700 disabled:opacity-60">{busy ? "Saving..." : "Save profile"}</button>
    </div>
  );
}
